import { Box, Button, Card, Chip, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { Postrequestcall } from "../../request/Postrequest";
import { FETCH_JOB_POSTS_NEW_2 } from "../../constant/Apipath";
import PostAJobModal from "../job-posting/PostAJobModal";
import { getAddress } from "../../request/Fetchcurrentaddress";

const buildTagLabel = (tag) => tag?.typeName || tag?.tradeName || tag?.name || "";
const groupByCompany = (jobs = []) => {
  const map = {};
  jobs.forEach((job) => {
    const companyName = job?.companyName || job?.employerName || "Other";
    const key = `${companyName}-${job?.companyId || job?.employerId || job?.jobPostId || Math.random()}`;
    if (!map[key]) {
      map[key] = {
        key,
        companyName,
        jobs: []
      };
    }
    map[key].jobs.push(job);
  });
  return Object.values(map);
};

const JOBS_PAGE_SIZE = 20;

const SitesJobPostSection = ({ siteId, siteInfo, activeTab }) => {
  const { loginData } = useSelector((state) => state?.main);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [showPostJobModal, setShowPostJobModal] = useState(false);
  const navigate = useNavigate();

  const buildPayload = (page) => ({
    UserId: loginData?.userInformation?.UserId,
    PageNumber: page,
    PageSize: JOBS_PAGE_SIZE,
    Latitude: null,
    Longitude: null,
    SearchString: "",
    Types: "",
    Trades: "",
    Location: "",
    RadiusKm: 2,
    JobPostId: 0,
    SiteId: siteId ?? 0
  });

  const jobGroups = useMemo(() => groupByCompany(jobs), [jobs]);

  const fetchJobs = async (page = 1, append = false) => {
    if (!siteId || !loginData) return;
    if (!append) {
      setLoading(true);
      setError("");
    }
    try {
      const payload = buildPayload(page);
      const response = await Postrequestcall(
        FETCH_JOB_POSTS_NEW_2,
        payload,
        loginData?.accessToken
      );
      const newJobs = response?.data?.data || [];
      setJobs((prev) => (append ? [...prev, ...newJobs] : newJobs));
      setPageNumber(page);
      setHasMore(newJobs.length === JOBS_PAGE_SIZE);
      if (!newJobs.length && !append) {
        setError("No job postings found for this site.");
      }
    } catch (err) {
      console.error("SitesJobPostSection -> fetchJobs", err);
      if (!append) {
        setError("Failed to load job postings.");
        setJobs([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "job-postings" || !siteId || !loginData) {
      return;
    }
    setJobs([]);
    setPageNumber(1);
    setHasMore(true);
    setError("");
    fetchJobs(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, siteId, loginData]);

  const renderJobGroups = () => (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {jobGroups.map((group) => {
        const primaryJob = group.jobs[0];
        const extras = group.jobs.length - 1;
        const tags = [...(primaryJob?.jobPostTypes || []), ...(primaryJob?.trades || [])]
          .map(buildTagLabel)
          .filter(Boolean)
          .slice(0, 3);
        const location = primaryJob?.location || primaryJob?.jobCity || "";
        const jobTypeLabel =
          primaryJob?.jobPostTypes?.[0]?.typeName ||
          primaryJob?.jobType ||
          primaryJob?.typeName ||
          "";

        return (
          <Card
            key={group.key}
            variant="outlined"
            sx={{
              borderColor: "#e3e3e3",
              borderRadius: 2,
              boxShadow: "0 1px 4px rgba(0,0,0,0.05)"
            }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={2}
              sx={{ p: 2 }}>
              <Stack spacing={0.5} flex={1}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {primaryJob?.title || "Job Opening"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {group.companyName}
                </Typography>
                {location && (
                  <Typography variant="body2" color="text.secondary">
                    {location}
                  </Typography>
                )}
                {jobTypeLabel && (
                  <Typography variant="caption" color="text.secondary">
                    {jobTypeLabel}
                  </Typography>
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ mt: 1, pb: 0.5, gap: 0.75 }}>
                  {tags.map((tag, idx) => (
                    <Chip
                      key={`${group.key}-tag-${idx}`}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: "#d0d0d0", color: "#444" }}
                    />
                  ))}
                </Stack>
              </Stack>
              <Stack spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                {extras > 0 && (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "#e6f2ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      +{extras}
                    </Typography>
                  </Box>
                )}
                <IconButton
                  size="small"
                  onClick={() => navigate("/job-postings", { state: { relatedSiteId: siteId } })}>
                  <ChevronRightIcon sx={{ color: "#1976d2" }} />
                </IconButton>
              </Stack>
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );

  if (loading && !jobs.length) {
    return (
      <Stack spacing={2} sx={{ pt: 1 }}>
        {[...Array(3)].map((_, idx) => (
          <Card key={idx} variant="outlined">
            <Stack spacing={1} sx={{ p: 2 }}>
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="45%" />
              <Skeleton variant="rectangular" height={50} />
            </Stack>
          </Card>
        ))}
      </Stack>
    );
  }

  const renderSiteAddress = () => {
    if (!siteInfo) return "";

    const hasAddress = siteInfo.address?.trim() !== "";
    const hasCity = siteInfo.city?.trim() !== "";
    const hasProvince = siteInfo.province?.trim() !== "";

    if (hasAddress && hasCity) {
      return `${siteInfo.address?.trim()}, ${siteInfo.city.trim()}`;
    } else if (hasAddress) {
      return siteInfo.address.trim();
    } else if (hasCity && hasProvince) {
      return `${siteInfo.city.trim()}, ${siteInfo.province.trim()}`;
    } else if (hasCity) {
      return siteInfo.city.trim();
    } else {
      return "";
    }
  };

  const openJobpost = async () => {
    setShowPostJobModal(true);
  };

  return (
    <>
      {console.log("siteInfo", siteInfo)}
      <Stack sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openJobpost}
          sx={{ alignSelf: "end" }}>
          Add Job Post
        </Button>
        {!jobs.length && error && (
          <Typography variant="body2" color="text.secondary">
            No job postings are available for this site.
          </Typography>
        )}
      </Stack>
      <InfiniteScroll
        dataLength={jobs.length}
        next={() => fetchJobs(pageNumber + 1, true)}
        hasMore={hasMore}
        loader={
          <Stack sx={{ py: 2 }} alignItems="center">
            <Skeleton variant="rectangular" width="60%" height={30} />
          </Stack>
        }
        style={{ overflow: "visible" }}
        endMessage={
          <Typography variant="body2" color="text.secondary" sx={{ pt: 2, textAlign: "center" }}>
            {error ? "" : "You're all caught up."}
          </Typography>
        }>
        {renderJobGroups()}
      </InfiniteScroll>

      <PostAJobModal
        open={showPostJobModal}
        onClose={() => setShowPostJobModal(false)}
        onNewJobPostedSuccess={() => {
          setShowPostJobModal(false);
          fetchJobs(1, false);
        }}
        initialSite={siteInfo}
        initialLocation={{
          address: renderSiteAddress(),
          lat: siteInfo?.latitude || null,
          lng: siteInfo?.longitude || null
        }}
      />
    </>
  );
};

export default SitesJobPostSection;
